import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AlertType } from '../_models';

@Injectable()
export class AlertService {
  private subject = new BehaviorSubject<any[]>([]);
  private keepAfterNavigationChange = false;
  private messageId = 0;
  private currentTimeout: any;

  constructor(private router: Router) {
    // clear alert message on route change
    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.keepAfterNavigationChange) {
          // only keep for a single location change
          this.keepAfterNavigationChange = false;
        } else {
          // clear alert
          this.clearAllTimeouts();
          this.subject.next([]);
        }
      }
    });
  }

  success(message: string, keepAfterNavigationChange = false, subtext?: string) {
    this.keepAfterNavigationChange = keepAfterNavigationChange;
    const alertMessage = { id: ++this.messageId, type: AlertType.Success, text: message, subtext };
    const currentMessages = this.subject.value;
    this.subject.next([...currentMessages, alertMessage]);
    this.startSequentialTimer();
  }

  error(message: string, keepAfterNavigationChange = false, subtext?: string) {
    this.keepAfterNavigationChange = keepAfterNavigationChange;
    const alertMessage = { id: ++this.messageId, type: AlertType.Error, text: message, subtext };
    const currentMessages = this.subject.value;
    this.subject.next([...currentMessages, alertMessage]);
    this.startSequentialTimer();
  }

  private startSequentialTimer() {
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
    }
    
    this.currentTimeout = setTimeout(() => {
      this.removeLastMessage();
    }, 6000);
  }

  private removeLastMessage() {
    const currentMessages = this.subject.value;
    if (currentMessages.length > 0) {
      // Remove the last (most recent) message
      const updatedMessages = currentMessages.slice(0, -1);
      this.subject.next(updatedMessages);
      
      // If there are still messages, start timer for the next one
      if (updatedMessages.length > 0) {
        this.startSequentialTimer();
      }
    }
  }

  removeMessage(messageId: number) {
    const currentMessages = this.subject.value;
    const messageIndex = currentMessages.findIndex(msg => msg.id === messageId);
    
    if (messageIndex !== -1) {
      const isLastMessage = messageIndex === currentMessages.length - 1;
      const filteredMessages = currentMessages.filter(msg => msg.id !== messageId);
      this.subject.next(filteredMessages);
      
      // If we removed the last message and there are still messages, restart timer
      if (isLastMessage && filteredMessages.length > 0) {
        this.startSequentialTimer();
      } else if (filteredMessages.length === 0) {
        this.clearAllTimeouts();
      }
    }
  }

  private clearAllTimeouts() {
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
  }

  getMessage(): Observable<any[]> {
    return this.subject.asObservable();
  }
}
